<script>
import BackofficeLayout from '@/Layouts/BackofficeLayout.vue';

export default {
  layout: BackofficeLayout,
};
</script>

<script setup>
import { ref } from 'vue';
import { Head, useForm, router } from '@inertiajs/vue3';
import { Smartphone, Plus, Search, Edit2, Trash2, Power, Filter } from '@lucide/vue';

const props = defineProps({
  prefixes: Array,
  operators: Array,
  filters: Object,
});

const search = ref(props.filters?.search || '');
const selectedOperatorId = ref(props.filters?.mobile_operator_id || '');
const isModalOpen = ref(false);
const editingPrefix = ref(null);

const form = useForm({
  mobile_operator_id: props.operators[0]?.id || '',
  prefix: '',
  sort_order: 0,
  is_active: true,
});

function handleFilter() {
  router.get(route('admin.catalogue.operator-prefixes.index'), {
    search: search.value,
    mobile_operator_id: selectedOperatorId.value,
  }, { preserveState: true, replace: true });
}

function openCreateModal() {
  editingPrefix.value = null;
  form.reset();
  form.mobile_operator_id = selectedOperatorId.value || props.operators[0]?.id || '';
  form.clearErrors();
  isModalOpen.value = true;
}

function openEditModal(item) {
  editingPrefix.value = item;
  form.mobile_operator_id = item.mobile_operator_id;
  form.prefix = item.prefix;
  form.sort_order = item.sort_order;
  form.is_active = item.is_active;
  form.clearErrors();
  isModalOpen.value = true;
}

function savePrefix() {
  if (editingPrefix.value) {
    form.put(route('admin.catalogue.operator-prefixes.update', editingPrefix.value.id), {
      onSuccess: () => {
        isModalOpen.value = false;
      },
    });
  } else {
    form.post(route('admin.catalogue.operator-prefixes.store'), {
      onSuccess: () => {
        isModalOpen.value = false;
      },
    });
  }
}

function toggleActive(item) {
  router.patch(route('admin.catalogue.operator-prefixes.toggle-active', item.id));
}

function deletePrefix(item) {
  if (confirm(`Apakah Anda yakin ingin menghapus prefix '${item.prefix}'?`)) {
    router.delete(route('admin.catalogue.operator-prefixes.destroy', item.id));
  }
}
</script>

<template>
  <Head title="Manajemen Prefix Operator" />

  <div class="space-y-6">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div>
        <h1 class="text-xl font-bold text-slate-900 flex items-center gap-2">
          <Smartphone class="w-5 h-5 text-indigo-600" />
          <span>Prefix Nomor Seluler</span>
        </h1>
        <p class="text-xs text-slate-500 mt-0.5">Kelola mapping prefix 4 digit nomor HP (0811, 0812, 0857, dll) ke operator master</p>
      </div>

      <button
        @click="openCreateModal"
        type="button"
        class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs shadow-sm transition"
      >
        <Plus class="w-4 h-4" />
        <span>Tambah Prefix</span>
      </button>
    </div>

    <!-- Filter & Search Bar -->
    <div class="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs flex flex-col sm:flex-row items-center gap-3">
      <div class="relative flex-1 w-full">
        <Search class="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
        <input
          v-model="search"
          @keyup.enter="handleFilter"
          type="text"
          placeholder="Cari prefix (misal: 0812)..."
          class="w-full pl-10 pr-4 py-2 text-xs rounded-xl border border-slate-300 focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 outline-none"
        />
      </div>

      <div class="w-full sm:w-auto">
        <select
          v-model="selectedOperatorId"
          @change="handleFilter"
          class="w-full px-3 py-2 text-xs rounded-xl border border-slate-300 focus:border-indigo-600 outline-none bg-white font-medium text-slate-700"
        >
          <option value="">Semua Operator</option>
          <option v-for="op in operators" :key="op.id" :value="op.id">{{ op.name }} ({{ op.code }})</option>
        </select>
      </div>

      <button @click="handleFilter" type="button" class="w-full sm:w-auto px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition">
        Filter
      </button>
    </div>

    <!-- Prefixes Table -->
    <div class="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full text-left border-collapse text-xs">
          <thead>
            <tr class="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider text-[10px]">
              <th class="py-3 px-4">Prefix</th>
              <th class="py-3 px-4">Operator</th>
              <th class="py-3 px-4">Kode Operator</th>
              <th class="py-3 px-4 text-center">Urutan</th>
              <th class="py-3 px-4 text-center">Status</th>
              <th class="py-3 px-4 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100 font-medium text-slate-800">
            <tr v-for="p in prefixes" :key="p.id" class="hover:bg-slate-50/80 transition">
              <td class="py-3 px-4 font-mono font-black text-indigo-600 text-sm">{{ p.prefix }}</td>
              <td class="py-3 px-4 font-bold text-slate-900">{{ p.operator?.display_name || p.operator?.name || '-' }}</td>
              <td class="py-3 px-4 font-mono text-slate-500">{{ p.operator?.code || '-' }}</td>
              <td class="py-3 px-4 text-center font-mono">{{ p.sort_order }}</td>
              <td class="py-3 px-4 text-center">
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold"
                  :class="p.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'"
                >
                  {{ p.is_active ? 'Aktif' : 'Nonaktif' }}
                </span>
              </td>
              <td class="py-3 px-4 text-right space-x-1">
                <button
                  @click="toggleActive(p)"
                  title="Toggle Aktif"
                  type="button"
                  class="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 text-slate-600 transition"
                >
                  <Power class="w-3.5 h-3.5" :class="p.is_active ? 'text-emerald-600' : 'text-slate-400'" />
                </button>
                <button
                  @click="openEditModal(p)"
                  title="Edit Prefix"
                  type="button"
                  class="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 text-indigo-600 transition"
                >
                  <Edit2 class="w-3.5 h-3.5" />
                </button>
                <button
                  @click="deletePrefix(p)"
                  title="Hapus Prefix"
                  type="button"
                  class="p-1.5 rounded-lg border border-slate-200 hover:bg-rose-50 text-rose-600 transition"
                >
                  <Trash2 class="w-3.5 h-3.5" />
                </button>
              </td>
            </tr>
            <tr v-if="prefixes.length === 0">
              <td colspan="6" class="py-8 text-center text-slate-400 italic">Belum ada data prefix operator.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Modal Form Prefix -->
    <div v-if="isModalOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-xs">
      <div class="bg-white rounded-3xl p-6 max-w-md w-full shadow-xl space-y-5">
        <h3 class="text-base font-bold text-slate-900">
          {{ editingPrefix ? 'Edit Prefix Operator' : 'Tambah Prefix Operator' }}
        </h3>

        <form @submit.prevent="savePrefix" class="space-y-4 text-xs">
          <div>
            <label class="block font-semibold text-slate-700 mb-1">Operator Seluler <span class="text-rose-500">*</span></label>
            <select
              v-model="form.mobile_operator_id"
              class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 font-medium focus:border-indigo-600 outline-none"
            >
              <option v-for="op in operators" :key="op.id" :value="op.id">{{ op.name }} ({{ op.code }})</option>
            </select>
            <span v-if="form.errors.mobile_operator_id" class="text-rose-500 text-[11px] block mt-0.5">{{ form.errors.mobile_operator_id }}</span>
          </div>

          <div>
            <label class="block font-semibold text-slate-700 mb-1">Prefix 4 Digit <span class="text-rose-500">*</span></label>
            <input
              v-model="form.prefix"
              type="text"
              placeholder="Contoh: 0812"
              class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 font-mono font-bold focus:border-indigo-600 outline-none"
            />
            <span v-if="form.errors.prefix" class="text-rose-500 text-[11px] block mt-0.5">{{ form.errors.prefix }}</span>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block font-semibold text-slate-700 mb-1">Urutan</label>
              <input
                v-model="form.sort_order"
                type="number"
                class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 focus:border-indigo-600 outline-none"
              />
            </div>

            <div class="flex items-center pt-6">
              <label class="inline-flex items-center gap-2 cursor-pointer">
                <input v-model="form.is_active" type="checkbox" class="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500" />
                <span class="font-bold text-slate-700">Status Aktif</span>
              </label>
            </div>
          </div>

          <div class="pt-2 flex justify-end gap-3">
            <button
              @click="isModalOpen = false"
              type="button"
              class="px-4 py-2.5 rounded-xl border border-slate-300 text-slate-700 font-bold transition"
            >
              Batal
            </button>
            <button
              :disabled="form.processing"
              type="submit"
              class="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold transition"
            >
              Simpan
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
